import { useEffect, useCallback, useRef } from 'react'
import { useLocation } from 'react-router'
import { useAnalytics } from '../services/analyticsService'
import { useAuthStore } from '../stores/auth'
import { getRouteTemplate } from '../utils/routeTemplate'

// Hook for automatic page view tracking
export function usePageViewTracking() {
  const location = useLocation()
  const { trackPageView } = useAnalytics()
  const currentTherapist = useAuthStore((s) => s.currentTherapist)

  useEffect(() => {
    const pageTitle = document.title
    const routeTemplate = getRouteTemplate(location.pathname)
    const userType = currentTherapist ? 'therapist' : 'anonymous'

    trackPageView({
      page_title: pageTitle,
      page_location: `${window.location.origin}${routeTemplate}`,
      page_path: routeTemplate,
      user_type: userType,
    })
  }, [location, trackPageView, currentTherapist])
}

// Hook for user engagement tracking
export function useEngagementTracking() {
  const { trackUserEngagement } = useAnalytics()
  const currentTherapist = useAuthStore((s) => s.currentTherapist)
  const startTime = useRef(Date.now())
  const lastActivity = useRef(Date.now())

  const trackEngagement = useCallback(() => {
    const engagementTime = Date.now() - startTime.current
    const userType = currentTherapist ? 'therapist' : 'anonymous'

    trackUserEngagement({
      engagement_time_msec: engagementTime,
      user_type: userType,
    })
  }, [trackUserEngagement, currentTherapist])

  // Track engagement on user activity
  useEffect(() => {
    const handleActivity = () => {
      lastActivity.current = Date.now()
    }

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart']
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true })
    })

    // Track engagement every 30 seconds
    const interval = setInterval(() => {
      const timeSinceLastActivity = Date.now() - lastActivity.current
      if (timeSinceLastActivity < 30000) { // User was active in last 30 seconds
        trackEngagement()
      }
    }, 30000)

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity)
      })
      clearInterval(interval)
    }
  }, [trackEngagement])

  return { trackEngagement }
}

// Hook for onboarding flow tracking
export function useOnboardingTracking() {
  const { trackOnboardingStep } = useAnalytics()
  const currentTherapist = useAuthStore((s) => s.currentTherapist)
  const stepStartTime = useRef<number>(0)

  const startStep = useCallback((stepName: string, stepNumber: number) => {
    stepStartTime.current = Date.now()
    
    trackOnboardingStep({
      step_name: stepName,
      step_number: stepNumber,
      user_type: currentTherapist ? 'therapist' : 'anonymous',
    })
  }, [trackOnboardingStep, currentTherapist])

  const completeStep = useCallback((stepName: string, stepNumber: number) => {
    const completionTime = Date.now() - stepStartTime.current
    
    trackOnboardingStep({
      step_name: `${stepName}_completed`,
      step_number: stepNumber,
      completion_time: completionTime,
      user_type: currentTherapist ? 'therapist' : 'anonymous',
    })
  }, [trackOnboardingStep, currentTherapist])

  const skipStep = useCallback((stepName: string, stepNumber: number) => {
    trackOnboardingStep({
      step_name: `${stepName}_skipped`,
      step_number: stepNumber,
      user_type: currentTherapist ? 'therapist' : 'anonymous',
    })
  }, [trackOnboardingStep, currentTherapist])

  return { startStep, completeStep, skipStep }
}

// Hook for dashboard interaction tracking
export function useDashboardTracking() {
  const { trackDashboardInteraction, trackEvent } = useAnalytics()
  const currentTherapist = useAuthStore((s) => s.currentTherapist)

  const trackClick = useCallback((action: string, feature: string, value?: string | number) => {
    trackDashboardInteraction({
      action,
      feature,
      value,
      user_type: currentTherapist ? 'therapist' : 'anonymous',
    })
  }, [trackDashboardInteraction, currentTherapist])

  const trackView = useCallback((feature: string) => {
    trackDashboardInteraction({
      action: 'view',
      feature,
      user_type: currentTherapist ? 'therapist' : 'anonymous',
    })
  }, [trackDashboardInteraction, currentTherapist])

  const trackSearch = useCallback((searchTerm: string, resultsCount: number) => {
    trackDashboardInteraction({
      action: 'search',
      feature: 'search',
      value: `${searchTerm}:${resultsCount}`,
      user_type: currentTherapist ? 'therapist' : 'anonymous',
    })
  }, [trackDashboardInteraction, currentTherapist])

  const trackFilter = useCallback((filterType: string, filterValue: string) => {
    trackDashboardInteraction({
      action: 'filter',
      feature: filterType,
      value: filterValue,
      user_type: currentTherapist ? 'therapist' : 'anonymous',
    })
  }, [trackDashboardInteraction, currentTherapist])

  const trackDashboardView = useCallback((section: string, clientsCount?: number) => {
    trackEvent({
      name: 'dashboard_view',
      parameters: {
        section,
        clients_count: clientsCount,
        user_type: currentTherapist ? 'therapist' : 'anonymous',
      }
    })
  }, [trackEvent, currentTherapist])

  const trackDashboardSectionClick = useCallback((section: string, targetSection: string) => {
    trackEvent({
      name: 'dashboard_section_click',
      parameters: {
        section,
        target_section: targetSection,
        user_type: currentTherapist ? 'therapist' : 'anonymous',
      }
    })
  }, [trackEvent, currentTherapist])

  const trackClientListView = useCallback((viewType: string, totalClients: number, filteredCount?: number) => {
    trackEvent({
      name: 'client_list_view',
      parameters: {
        view_type: viewType,
        total_clients: totalClients,
        filtered_count: filteredCount ?? totalClients,
        user_type: currentTherapist ? 'therapist' : 'anonymous',
      }
    })
  }, [trackEvent, currentTherapist])

  const trackClientFilter = useCallback((filterType: string, filterValue: string, resultsCount: number) => {
    trackEvent({
      name: 'client_filter_applied',
      parameters: {
        filter_type: filterType,
        filter_value: filterValue,
        results_count: resultsCount,
        user_type: currentTherapist ? 'therapist' : 'anonymous',
      }
    })
  }, [trackEvent, currentTherapist])

  return { 
    trackClick, 
    trackView, 
    trackSearch, 
    trackFilter,
    trackDashboardView,
    trackDashboardSectionClick,
    trackClientListView,
    trackClientFilter
  }
}

// Hook for error tracking
export function useErrorTracking() {
  const { trackError } = useAnalytics()
  const currentTherapist = useAuthStore((s) => s.currentTherapist)

  const trackErrorEvent = useCallback((
    errorCode: string = 'unknown',
    _unused?: string,
    errorCategory: string = 'runtime'
  ) => {
    trackError({
      error_code: errorCode,
      surface: errorCategory,
      user_type: currentTherapist ? 'therapist' : 'anonymous',
    })
  }, [trackError, currentTherapist])

  return { trackErrorEvent }
}

// Hook for feature usage tracking
export function useFeatureTracking() {
  const { trackFeatureUsage } = useAnalytics()
  const currentTherapist = useAuthStore((s) => s.currentTherapist)

  const trackFeature = useCallback((
    featureName: string,
    featureCategory: string,
    usageCount: number = 1
  ) => {
    trackFeatureUsage({
      feature_name: featureName,
      feature_category: featureCategory,
      usage_count: usageCount,
      user_type: currentTherapist ? 'therapist' : 'anonymous',
    })
  }, [trackFeatureUsage, currentTherapist])

  return { trackFeature }
}

// Hook for conversion tracking
export function useConversionTracking() {
  const { trackConversion } = useAnalytics()
  const currentTherapist = useAuthStore((s) => s.currentTherapist)

  const trackConversionEvent = useCallback((
    conversionType: string,
    conversionValue?: number,
    currency: string = 'USD'
  ) => {
    trackConversion({
      conversion_type: conversionType,
      conversion_value: conversionValue,
      currency,
      user_type: currentTherapist ? 'therapist' : 'anonymous',
    })
  }, [trackConversion, currentTherapist])

  return { trackConversionEvent }
}

// Hook for form tracking
export function useFormTracking() {
  const { trackEvent } = useAnalytics()
  const currentTherapist = useAuthStore((s) => s.currentTherapist)

  const trackFormStart = useCallback((formName: string) => {
    trackEvent({
      name: 'form_start',
      parameters: {
        form_name: formName,
        user_type: currentTherapist ? 'therapist' : 'anonymous',
      }
    })
  }, [trackEvent, currentTherapist])

  const trackFormSubmit = useCallback((formName: string, success: boolean, fields?: string[]) => {
    trackEvent({
      name: 'form_submit',
      parameters: {
        form_name: formName,
        success,
        fields: fields?.join(','),
        user_type: currentTherapist ? 'therapist' : 'anonymous',
      }
    })
  }, [trackEvent, currentTherapist])

  const trackFormAbandon = useCallback((formName: string, fieldsCompleted: string[]) => {
    trackEvent({
      name: 'form_abandon',
      parameters: {
        form_name: formName,
        fields_completed: fieldsCompleted.join(','),
        user_type: currentTherapist ? 'therapist' : 'anonymous',
      }
    })
  }, [trackEvent, currentTherapist])

  return { trackFormStart, trackFormSubmit, trackFormAbandon }
}

// Hook for button click tracking
export function useButtonTracking() {
  const { trackEvent } = useAnalytics()
  const currentTherapist = useAuthStore((s) => s.currentTherapist)

  const trackButtonClick = useCallback((
    buttonName: string,
    location: string,
    context?: Record<string, any>
  ) => {
    trackEvent({
      name: 'button_click',
      parameters: {
        button_name: buttonName,
        location,
        ...context,
        user_type: currentTherapist ? 'therapist' : 'anonymous',
      }
    })
  }, [trackEvent, currentTherapist])

  return { trackButtonClick }
}

// Hook for search tracking
export function useSearchTracking() {
  const { trackEvent } = useAnalytics()
  const currentTherapist = useAuthStore((s) => s.currentTherapist)

  const trackSearch = useCallback((
    searchTerm: string,
    resultsCount: number,
    searchType: string = 'general'
  ) => {
    trackEvent({
      name: 'search',
      parameters: {
        search_term: searchTerm,
        results_count: resultsCount,
        search_type: searchType,
        user_type: currentTherapist ? 'therapist' : 'anonymous',
      }
    })
  }, [trackEvent, currentTherapist])

  const trackSearchNoResults = useCallback((searchTerm: string, searchType: string = 'general') => {
    trackEvent({
      name: 'search_no_results',
      parameters: {
        search_term: searchTerm,
        search_type: searchType,
        user_type: currentTherapist ? 'therapist' : 'anonymous',
      }
    })
  }, [trackEvent, currentTherapist])

  const trackClientSearch = useCallback((searchTerm: string, resultsCount: number) => {
    trackEvent({
      name: 'client_search',
      parameters: {
        search_term_length: searchTerm.length,
        results_count: resultsCount,
        user_type: currentTherapist ? 'therapist' : 'anonymous',
      }
    })
  }, [trackEvent, currentTherapist])

  return { trackSearch, trackSearchNoResults, trackClientSearch }
}
